import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, MessageSquare, Play, Mail, Phone, MessagesSquare, Bell } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const LANGS = [
  { k: "en", label: "English",   required: true },
  { k: "hi", label: "हिन्दी" },
  { k: "kn", label: "ಕನ್ನಡ" },
  { k: "ta", label: "தமிழ்" },
  { k: "ml", label: "മലയാളം" },
];
const CHANNELS = [
  { k: "email",    label: "Email",     Icon: Mail },
  { k: "sms",      label: "SMS",       Icon: Phone },
  { k: "whatsapp", label: "WhatsApp",  Icon: MessagesSquare },
  { k: "in_app",   label: "In-App",    Icon: Bell },
];

const EMPTY = {
  key: "", channel: "email",
  subject: { en: "", hi: "", kn: "", ta: "", ml: "" },
  body:    { en: "", hi: "", kn: "", ta: "", ml: "" },
  active: true,
};

const ChannelPill = ({ channel }) => {
  const c = CHANNELS.find((x) => x.k === channel) || CHANNELS[0];
  const Icon = c.Icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#457b9d]/10 text-[#457b9d]">
      <Icon size={10} /> {c.label}
    </span>
  );
};

export default function AdminNotificationTemplates() {
  const { user: me } = useAuth();
  const canEdit = ["platform_owner", "translation_admin"].includes(me?.admin_role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [channelF, setChannelF] = useState("");
  const [missingOnly, setMissingOnly] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [previewLocale, setPreviewLocale] = useState("en");
  const [previewData, setPreviewData] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (channelF) params.channel = channelF;
    if (missingOnly) params.missing_only = true;
    api.get("/admin/notification-templates", { params })
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load();   }, [channelF, missingOnly]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
     
  }, [q]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setPreviewData(null); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      key: t.key, channel: t.channel,
      subject: { ...EMPTY.subject, ...(t.subject || {}) },
      body: { ...EMPTY.body, ...(t.body || {}) },
      active: !!t.active,
    });
    setPreviewData(null);
    setOpen(true);
  };
  const save = async () => {
    if (!form.key.trim() || !form.body?.en?.trim()) { toast.error("Key and English body are required"); return; }
    try {
      if (editing) {
        await api.patch(`/admin/notification-templates/${editing.id}`, form);
        toast.success("Template saved");
      } else {
        await api.post("/admin/notification-templates", form);
        toast.success("Template created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.key}"?`)) return;
    try {
      await api.delete(`/admin/notification-templates/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const previewNow = async () => {
    if (!editing?.id && !form.key) { toast.error("Save the template first"); return; }
    const tid = editing?.id;
    if (!tid) { toast.error("Save the template first, then preview"); return; }
    try {
      const r = await api.post(`/admin/notification-templates/${tid}/preview`, {
        locale: previewLocale,
        variables: {
          customer_name: "Rahul Verma", vehicle_reg: "KA-01-AB-1234",
          garage_name: "Demo Auto Works", job_number: "JC-00042",
          share_url: "https://fenderapp.com/j/abc", amount_due: "3,450",
          amount: "3,450", approve_url: "https://fenderapp.com/e/xyz",
          amount_paid: "3,450", invoice_number: "INV-00019",
          case_number: "SC-20260805-XXXXXX", subject: "Login failing",
          reply_body: "Please try clearing your browser cache.",
          week_start: "2026-07-29", week_end: "2026-08-05",
          recipient_name: "Aditi", garages_registered: "17",
          active_garages_this_week: "1", jc_this_week: "7",
        },
      });
      setPreviewData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const missing = (t) => LANGS.slice(1).filter((l) => !(t.body || {})[l.k]).length;

  return (
    <div className="space-y-5" data-testid="admin-templates-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Master Data</div>
          <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557] flex items-center gap-2">
            <MessageSquare size={22} className="text-[#457b9d]" /> Notification Templates
          </h1>
          <p className="text-[13px] text-[#667085] mt-0.5">
            {items.length} template(s) across email / SMS / WhatsApp / in-app in 5 languages. Use <code className="font-mono text-[11px] text-[#e63946]">&#123;&#123;variable&#125;&#125;</code> placeholders.
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={openNew}
            data-testid="admin-templates-new"
            className="h-9 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold text-[13px] px-3"
          >
            <Plus size={14} className="mr-1.5" /> New template
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={channelF}
          onChange={(e) => setChannelF(e.target.value)}
          data-testid="admin-templates-channel-filter"
          className="h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] text-[#1d3557]"
        >
          <option value="">All channels</option>
          {CHANNELS.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 text-[13px] text-[#1d3557] cursor-pointer">
          <input
            type="checkbox"
            checked={missingOnly}
            onChange={(e) => setMissingOnly(e.target.checked)}
            data-testid="admin-templates-missing-only"
            className="accent-[#e63946]"
          />
          Rows with missing translations
        </label>
        <Input
          data-testid="admin-templates-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search key…"
          className="flex-1 min-w-[220px] max-w-md h-9 rounded-md text-[13px]"
        />
      </div>

      <div className="bg-white border border-[#E4E7EC] rounded-lg overflow-hidden" data-testid="admin-templates-table">
        <table className="w-full text-[13px]">
          <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Key</th>
              <th className="text-left px-4 py-3 font-semibold">Channel</th>
              <th className="text-left px-4 py-3 font-semibold">EN preview</th>
              <th className="text-left px-4 py-3 font-semibold">Translations</th>
              {canEdit && <th className="text-right px-4 py-3 font-semibold">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1FAEE]">
            {loading && <tr><td colSpan={5} className="px-4 py-12 text-center text-[13px] text-[#667085]">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-[13px] text-[#667085]">No templates.</td></tr>
            )}
            {items.map((t) => {
              const miss = missing(t);
              return (
                <tr key={t.id} data-testid={`admin-template-row-${t.id}`} className="hover:bg-[#F7F9FC]">
                  <td className="px-4 py-3 font-mono text-[12px] text-[#1d3557] font-semibold">{t.key}</td>
                  <td className="px-4 py-3"><ChannelPill channel={t.channel} /></td>
                  <td className="px-4 py-3 text-[12px] text-[#1d3557] max-w-md truncate" title={t.body?.en}>{t.body?.en || <span className="text-[#a8dadc]">—</span>}</td>
                  <td className="px-4 py-3">
                    <div className="inline-flex items-center gap-1">
                      {LANGS.slice(1).map((l) => (
                        <span
                          key={l.k}
                          title={`${l.label}: ${t.body?.[l.k] || "missing"}`}
                          className={`w-6 h-5 grid place-items-center rounded text-[9px] font-bold uppercase tracking-wider ${
                            t.body?.[l.k] ? "bg-[#12B76A]/10 text-[#12B76A]" : "bg-[#F79009]/10 text-[#F79009]"
                          }`}
                        >
                          {l.k}
                        </span>
                      ))}
                      {miss > 0 && <span className="ml-1 text-[10px] font-bold text-[#F79009]">{miss} missing</span>}
                    </div>
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-3">
                        <button onClick={() => openEdit(t)} data-testid={`admin-template-edit-${t.id}`} className="text-[12px] text-[#457b9d] hover:underline font-semibold inline-flex items-center gap-1">
                          <Pencil size={11} /> Edit
                        </button>
                        <button onClick={() => remove(t)} data-testid={`admin-template-delete-${t.id}`} className="text-[12px] text-[#e63946] hover:underline font-semibold inline-flex items-center gap-1">
                          <Trash2 size={11} /> Delete
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-lg border-[#E4E7EC] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1d3557] font-semibold">
              {editing ? `Edit template "${editing.key}"` : "New notification template"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Key</Label>
                <Input
                  data-testid="admin-template-key"
                  value={form.key}
                  onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
                  className="mt-1 rounded-md font-mono"
                  placeholder="e.g. job.checkin_confirmed"
                  disabled={!!editing}
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Channel</Label>
                <select
                  data-testid="admin-template-channel"
                  value={form.channel}
                  onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))}
                  className="mt-1 w-full h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] text-[#1d3557]"
                >
                  {CHANNELS.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
                </select>
              </div>
            </div>

            {LANGS.map((l) => (
              <div key={l.k} className="border border-[#E4E7EC] rounded-md p-3">
                <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold mb-2">
                  {l.label}{l.required ? " *" : ""}
                </div>
                <Label className="text-[10px] uppercase tracking-wider text-[#667085] font-semibold">Subject</Label>
                <Input
                  data-testid={`admin-template-subject-${l.k}`}
                  value={form.subject?.[l.k] || ""}
                  onChange={(e) => setForm((f) => ({ ...f, subject: { ...f.subject, [l.k]: e.target.value } }))}
                  className="mt-1 rounded-md"
                  placeholder={l.k === "en" ? "Subject line…" : "(optional)"}
                />
                <Label className="mt-2 block text-[10px] uppercase tracking-wider text-[#667085] font-semibold">Body</Label>
                <Textarea
                  data-testid={`admin-template-body-${l.k}`}
                  rows={l.k === "en" ? 3 : 2}
                  value={form.body?.[l.k] || ""}
                  onChange={(e) => setForm((f) => ({ ...f, body: { ...f.body, [l.k]: e.target.value } }))}
                  className="mt-1 rounded-md"
                  placeholder={l.k === "en" ? "Use {{variable}} placeholders" : "(optional)"}
                />
              </div>
            ))}

            <label className="flex items-center gap-2 text-[13px] text-[#1d3557] cursor-pointer">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                data-testid="admin-template-active"
                className="accent-[#e63946]"
              />
              Active — usable by workshop app + notifications
            </label>

            <div className="grid grid-cols-2 gap-2">
              <Button onClick={save} data-testid="admin-template-save" className="h-10 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold">
                {editing ? "Save changes" : "Create template"}
              </Button>
              {editing && (
                <div className="flex gap-2">
                  <select
                    value={previewLocale}
                    onChange={(e) => setPreviewLocale(e.target.value)}
                    data-testid="admin-template-preview-locale"
                    className="h-10 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] text-[#1d3557]"
                  >
                    {LANGS.map((l) => <option key={l.k} value={l.k}>{l.label}</option>)}
                  </select>
                  <Button
                    onClick={previewNow}
                    data-testid="admin-template-preview"
                    className="flex-1 h-10 bg-white border border-[#E4E7EC] hover:border-[#457b9d] text-[#1d3557] rounded-md font-semibold"
                  >
                    <Play size={13} className="mr-1.5" /> Preview
                  </Button>
                </div>
              )}
            </div>

            {previewData && (
              <div className="border border-[#F1FAEE] bg-[#F7F9FC] rounded-md p-3" data-testid="admin-template-preview-out">
                <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Rendered — {previewData.locale} · {previewData.channel}</div>
                {previewData.subject && (
                  <div className="mt-2">
                    <div className="text-[10px] uppercase tracking-wider text-[#667085] font-semibold">Subject</div>
                    <div className="text-[13px] text-[#1d3557] font-semibold">{previewData.subject}</div>
                  </div>
                )}
                <div className="mt-2">
                  <div className="text-[10px] uppercase tracking-wider text-[#667085] font-semibold">Body</div>
                  <div className="text-[13px] text-[#1d3557] whitespace-pre-wrap mt-1">{previewData.body}</div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
